import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

// ✅ Skeleton helpers
const SkeletonLine = ({ w = "100%", h = 14, mb = 8 }) => (
    <div className="sk" style={{ width: w, height: h, marginBottom: mb }} />
);

const HourlyTableSkeleton = ({ rows = 6 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <tr key={`hsk-${i}`}>
                <td><SkeletonLine w="55px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="70px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="70px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="90px" h={12} mb={0} /></td>
                <td style={{ textAlign: "right" }}><SkeletonLine w="55px" h={12} mb={0} /></td>
            </tr>
        ))}
    </>
);

const SkillCardSkeleton = () => (
    <div className="col-md-6 col-lg-4 mb-3">
        <div className="card shadow-sm">
            <div className="card-body p-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <SkeletonLine w="55%" h={16} mb={0} />
                    <SkeletonLine w="35%" h={12} mb={0} />
                </div>

                <div className="table-responsive" style={{ maxHeight: 260, overflowY: "auto" }}>
                    <table className="table table-sm table-bordered mb-0 mini-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>Hr</th>
                                <th style={{ textAlign: "right" }}>Calls</th>
                                <th style={{ textAlign: "right" }}>Orders</th>
                                <th style={{ textAlign: "right" }}>Spend</th>
                                <th style={{ textAlign: "right" }}>Perf</th>
                            </tr>
                        </thead>
                        <tbody>
                            <HourlyTableSkeleton rows={6} />
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
);

const HourlyPPCYesterdayReport = () => {
    const [rows, setRows] = useState([]);
    const [hours, setHours] = useState([]);
    const [summary, setSummary] = useState(null);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showSkills, setShowSkills] = useState(true);

    // ✅ Range states
    const [startHour, setStartHour] = useState(1);
    const [endHour, setEndHour] = useState(1);

    // ✅ Applied range (actual active filter)
    const [appliedRange, setAppliedRange] = useState({ start: 1, end: 1 });

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    // ✅ Default = current hour only
    useEffect(() => {
        const now = new Date();
        const h24 = now.getHours(); // 0-23
        const currentHour = h24 === 0 ? 24 : h24; // 1-24 feel

        setStartHour(currentHour);
        setEndHour(currentHour);
        setAppliedRange({ start: currentHour, end: currentHour });
    }, []);

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/hourly-yesterday-report");
    }, [ppcid]);

    // ✅ Load ONLY when appliedRange changes
    useEffect(() => {
        if (!ppcid) return;
        if (!appliedRange?.start || !appliedRange?.end) return;
        load(ppcid, appliedRange.start, appliedRange.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid, appliedRange]);

    const load = async (id, sh, eh) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-yesterday-live-call-hour-wise-with-proccess-name-by-id/${id}?start_hour=${sh}&end_hour=${eh}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                setRows(Array.isArray(p?.rows) ? p.rows : []);
                setHours(Array.isArray(p?.hours) ? p.hours : []);
                setSummary(p?.summary || null);
                setMeta(p?.meta || { start_hour: sh, end_hour: eh });
            } else {
                toast.error(res?.data?.message || "Failed to load hourly report");
                setRows([]);
                setHours([]);
                setSummary(null);
                setMeta(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading hourly report");
            setRows([]);
            setHours([]);
            setSummary(null);
            setMeta(null);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Apply range button
    const onApply = () => {
        const sh = Number(startHour);
        const eh = Number(endHour);

        if (!sh || !eh) return toast.error("Please select both From & To hour");
        if (sh > eh) return toast.error("From hour cannot be greater than To hour");

        setAppliedRange({ start: sh, end: eh });
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const top = useMemo(() => {
        const tCalls = summary?.total_calls ?? 0;
        const tOrders = summary?.total_orders ?? 0;
        const tSpend = summary?.total_spend ?? 0;
        const tPerf = summary?.perf ?? (tCalls ? (tOrders / tCalls) * 100 : 0);
        return { tCalls, tOrders, tSpend, tPerf };
    }, [summary]);

    const sortedRows = useMemo(() => {
        return [...(rows || [])].sort(
            (a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0)
        );
    }, [rows]);

    const rangeTitle = useMemo(() => {
        const sh = meta?.start_hour ?? appliedRange.start;
        const eh = meta?.end_hour ?? appliedRange.end;
        if (!sh || !eh) return "Yesterday Hourly";
        return `Yesterday Hourly (${sh}–${eh})`;
    }, [meta, appliedRange]);

    const isPendingChange = useMemo(() => {
        return (
            Number(startHour) !== Number(appliedRange.start) ||
            Number(endHour) !== Number(appliedRange.end)
        );
    }, [startHour, endHour, appliedRange]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::HOURLY REPORT</title>
            </Helmet>

            <style>{`
        .mini-table th,.mini-table td{font-size:12px; padding:8px 10px;}
        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .filterBox select{min-width:120px;}
        .pending{font-size:12px;color:#ff7a00;font-weight:600;}

        /* ✅ skeleton shimmer */
        .sk{position:relative; overflow:hidden; background:#eee; border-radius:6px;}
        .sk:after{
          content:""; position:absolute; inset:0; transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);
          animation:shimmer 1.2s infinite;
        }
        @keyframes shimmer{100%{transform:translateX(100%)}}
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                        <div className="d-flex justify-content-between">
                            <div className="mt-2">
                                <h5 className="livecallcount mb-0">
                                    {rangeTitle}{" "}
                                    {isLoading ? (
                                        <>
                                            {" | "}
                                            <span style={{ display: "inline-block", verticalAlign: "middle", width: 420 }}>
                                                <SkeletonLine w="100%" h={14} mb={0} />
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            | Calls: {fmt(top.tCalls)} | Orders: {fmt(top.tOrders)} | Spend: ₹{fmtMoney(top.tSpend)} | Perf:{" "}
                                            {Number(top.tPerf || 0).toFixed(1)}%
                                        </>
                                    )}
                                </h5>

                                {isPendingChange && !isLoading && (
                                    <div className="pending">
                                        Range changed (not applied). Click Apply to load {startHour}–{endHour}.
                                    </div>
                                )}
                            </div>

                            {/* ✅ RANGE FILTER */}
                            <div className="filterRow">
                                <div className="filterBox">
                                    <label>From Hour</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={startHour}
                                        onChange={(e) => setStartHour(Number(e.target.value))}
                                        disabled={isLoading}
                                    >
                                        {HOURS.map((h) => (
                                            <option key={h} value={h}>
                                                {h}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="filterBox">
                                    <label>To Hour</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={endHour}
                                        onChange={(e) => setEndHour(Number(e.target.value))}
                                        disabled={isLoading}
                                    >
                                        {HOURS.map((h) => (
                                            <option key={h} value={h}>
                                                {h}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button className="btnskillapply" onClick={onApply} disabled={isLoading}>
                                    Apply
                                </button>

                                <button className="btnskill" onClick={() => setShowSkills((s) => !s)} disabled={isLoading}>
                                    {showSkills ? "Hide Skill View" : "Show Skill View"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✅ Hour table (Filtered only) */}
                <div className="card mt-3 shadow-sm">
                    <div className="card-body p-2">
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0 mini-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>Hour</th>
                                        <th style={{ width: 120, textAlign: "right" }}>Calls</th>
                                        <th style={{ width: 120, textAlign: "right" }}>Orders</th>
                                        <th style={{ width: 140, textAlign: "right" }}>Spend</th>
                                        <th style={{ width: 100, textAlign: "right" }}>Perf</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HourlyTableSkeleton rows={Math.max(4, (hours?.length || 0) || 6)} />
                                    ) : hours?.length ? (
                                        hours.map((h, idx) => (
                                            <tr key={idx}>
                                                <td>{h.hour}</td>
                                                <td style={{ textAlign: "right" }}>{fmt(h.calls)}</td>
                                                <td style={{ textAlign: "right" }}>{fmt(h.orders)}</td>
                                                <td style={{ textAlign: "right" }}>₹{fmtMoney(h.spend)}</td>
                                                <td style={{ textAlign: "right" }}>{Number(h.perf || 0).toFixed(1)}%</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center">
                                                No hourly data in selected range
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ✅ Optional: Skill-wise hourly view */}
                {showSkills && (
                    <div className="row mt-3" style={{ marginBottom: "50px" }}>
                        {isLoading ? (
                            <>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <SkillCardSkeleton key={`csk-${i}`} />
                                ))}
                            </>
                        ) : (
                            sortedRows.map((r, idx) => (
                                <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                    <div className="card shadow-sm">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div style={{ fontWeight: 700 }} className="groupname">
                                                    {r.skillname}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                    Calls: {fmt(r.total_calls)} | Orders: {fmt(r.total_orders)} | ₹{fmtMoney(r.total_spend)}
                                                </div>
                                            </div>

                                            <div className="table-responsive" style={{ maxHeight: 260, overflowY: "auto" }}>
                                                <table className="table table-sm table-bordered mb-0 mini-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 60 }}>Hr</th>
                                                            <th style={{ textAlign: "right" }}>Calls</th>
                                                            <th style={{ textAlign: "right" }}>Orders</th>
                                                            <th style={{ textAlign: "right" }}>Spend</th>
                                                            <th style={{ textAlign: "right" }}>Perf</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(r.hours || []).length ? (
                                                            (r.hours || []).map((h, i2) => (
                                                                <tr key={i2}>
                                                                    <td>{h.hour}</td>
                                                                    <td style={{ textAlign: "right" }}>{fmt(h.calls)}</td>
                                                                    <td style={{ textAlign: "right" }}>{fmt(h.orders)}</td>
                                                                    <td style={{ textAlign: "right" }}>₹{fmtMoney(h.spend)}</td>
                                                                    <td style={{ textAlign: "right" }}>{Number(h.perf || 0).toFixed(1)}%</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={5} className="text-center">
                                                                    No skill hourly data
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        {!isLoading && (!rows || rows.length === 0) && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">No skill-wise hourly data.</div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(HourlyPPCYesterdayReport);
